import React, { useEffect, useRef } from "react";
import { FaPrint } from "react-icons/fa";

const InvoicePrintPreviewModal = ({
  open,
  html,
  loading = false,
  onClose,
  onPrint,
  title = "Print Preview",
  activePreview = "invoice",
  onPreviewChange,
  urdAvailable = false,
}) => {
  const iframeRef = useRef(null);
  const printBtnRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const timer = setTimeout(() => {
      printBtnRef.current?.focus();
    }, 100);

    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open || !html) return;

    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;

    if (!doc) return;

    doc.open();
    doc.write(html);
    doc.close();

    const timer = setTimeout(() => {
      printBtnRef.current?.focus();
    }, 150);

    return () => clearTimeout(timer);
  }, [open, html]);

  const handlePrint = () => {
    if (onPrint?.() === true) {
      setTimeout(() => {
        printBtnRef.current?.focus();
      }, 300);
      return;
    }

    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;

    iframe.contentWindow.focus();
    iframe.contentWindow.print();

    setTimeout(() => {
      printBtnRef.current?.focus();
    }, 300);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-3">
      <div className="bg-white w-full max-w-6xl h-[92vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 sm:px-4 sm:py-3 border-b bg-[#9481ce] text-white">
          <h3 className="shrink-0 text-sm font-semibold">{title}</h3>

          <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2">
            <div className="inline-flex rounded-lg bg-white/15 p-0.5">
              <button
                type="button"
                onClick={() => onPreviewChange?.("invoice")}
                className={`rounded-md px-2.5 py-1 text-[11px] font-semibold transition ${
                  activePreview === "invoice"
                    ? "bg-white text-[#5d4bb0] shadow-sm"
                    : "text-white hover:bg-white/10"
                }`}
              >
                Invoice Preview
              </button>
              <button
                type="button"
                disabled={!urdAvailable}
                onClick={() => onPreviewChange?.("urd")}
                className={`rounded-md px-2.5 py-1 text-[11px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-40 ${
                  activePreview === "urd"
                    ? "bg-white text-[#5d4bb0] shadow-sm"
                    : "text-white hover:bg-white/10"
                }`}
                title={urdAvailable ? "Preview URD receipt" : "No URD linked"}
              >
                URD Preview
              </button>
            </div>

            <button
              ref={printBtnRef}
              type="button"
              onClick={handlePrint}
              disabled={loading || !html}
              className="inline-flex shrink-0 items-center gap-2 px-3 py-1.5 rounded-full bg-white text-[#5d4bb0] text-xs font-semibold disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#9481ce]"
            >
              <FaPrint />
              Print / Save PDF
            </button>

            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 shrink-0 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-white"
              title="Close"
            >
              ×
            </button>
          </div>
        </div>

        <div className="flex-1 bg-slate-100 overflow-hidden">
          {loading ? (
            <div className="h-full flex items-center justify-center text-sm text-slate-500">
              Loading {activePreview === "urd" ? "URD" : "invoice"} preview...
            </div>
          ) : (
            <iframe
              ref={iframeRef}
              title={activePreview === "urd" ? "URD Preview" : "Invoice Preview"}
              className="w-full h-full border-0 bg-white"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoicePrintPreviewModal;
